import React, { useEffect, useState } from 'react';
import { SaveIcon, UsersIcon, BriefcaseIcon, BuildingIcon, MapPinIcon } from 'lucide-react';
import { CountrySelector } from '../settings/CountrySelector';
export function BankAccountForm({
  bankAccount,
  onSave,
  onCancel,
  locale
}) {
  const [formData, setFormData] = useState({
    id: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    swiftCode: '',
    iban: '',
    currency: 'USD',
    branchName: '',
    accountType: 'Checking',
    country: '',
    isPrimary: false
  });
  const accountTypes = ['Checking', 'Savings', 'Business', 'Current', 'Investment', 'Other'];
  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CNY', 'INR'];
  useEffect(() => {
    if (bankAccount) {
      setFormData({
        ...bankAccount
      });
    } else {
      setFormData({
        id: '',
        bankName: '',
        accountName: '',
        accountNumber: '',
        routingNumber: '',
        swiftCode: '',
        iban: '',
        currency: 'USD',
        branchName: '',
        accountType: 'Checking',
        country: '',
        isPrimary: false
      });
    }
  }, [bankAccount]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleCountryChange = countryCode => {
    setFormData({
      ...formData,
      country: countryCode
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    onSave(formData);
  };
  const getAssociationIcon = type => {
    switch (type) {
      case 'Vendor':
        return <BriefcaseIcon className="h-4 w-4 mr-2" data-id="element-8240" />;
      case 'Entity':
        return <BuildingIcon className="h-4 w-4 mr-2" data-id="element-8241" />;
      case 'Site':
        return <MapPinIcon className="h-4 w-4 mr-2" data-id="element-8242" />;
      case 'Org Unit':
        return <UsersIcon className="h-4 w-4 mr-2" data-id="element-8243" />;
      default:
        return <UsersIcon className="h-4 w-4 mr-2" data-id="element-8244" />;
    }
  };
  const getAssociationColor = type => {
    switch (type) {
      case 'Vendor':
        return 'bg-blue-100 text-blue-800';
      case 'Entity':
        return 'bg-purple-100 text-purple-800';
      case 'Site':
        return 'bg-green-100 text-green-800';
      case 'Org Unit':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="bg-white shadow sm:rounded-lg" data-id="element-8245">
      <div className="px-4 py-5 sm:p-6" data-id="element-8246">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-8247">
          {bankAccount ? 'Edit Bank Account' : 'Add Bank Account'}
        </h3>

        {/* Show associations if editing existing account */}
        {bankAccount && bankAccount.associations && bankAccount.associations.length > 0 && <div className="mt-4 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-8248">
              <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-8249">
                Used In ({bankAccount.associations.length})
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-8250">
                {bankAccount.associations.map((assoc, idx) => <span key={idx} className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${getAssociationColor(assoc.type)}`} data-id="element-8251">
                    {getAssociationIcon(assoc.type)}
                    {assoc.name}
                  </span>)}
              </div>
              <p className="mt-2 text-xs text-gray-500" data-id="element-8252">
                This bank account is currently being used by the above entities.
                Changes will affect all associated records.
              </p>
            </div>}

        <form className="mt-5 space-y-6" onSubmit={handleSubmit} data-id="element-8253">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-8254">
            <div className="sm:col-span-3" data-id="element-8255">
              <label htmlFor="bankName" className="block text-sm font-medium text-gray-700" data-id="element-8256">
                Bank Name
              </label>
              <div className="mt-1" data-id="element-8257">
                <input type="text" name="bankName" id="bankName" value={formData.bankName} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-8258" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-8259">
              <label htmlFor="accountName" className="block text-sm font-medium text-gray-700" data-id="element-8260">
                Account Name
              </label>
              <div className="mt-1" data-id="element-8261">
                <input type="text" name="accountName" id="accountName" value={formData.accountName} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-8262" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-8263">
              <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700" data-id="element-8264">
                Account Number
              </label>
              <div className="mt-1" data-id="element-8265">
                <input type="text" name="accountNumber" id="accountNumber" value={formData.accountNumber} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-8266" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-8267">
              <label htmlFor="accountType" className="block text-sm font-medium text-gray-700" data-id="element-8268">
                Account Type
              </label>
              <div className="mt-1" data-id="element-8269">
                <select id="accountType" name="accountType" value={formData.accountType} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8270">
                  {accountTypes.map(type => <option key={type} value={type} data-id="element-8271">
                      {type}
                    </option>)}
                </select>
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-8272">
              <label htmlFor="routingNumber" className="block text-sm font-medium text-gray-700" data-id="element-8273">
                Routing Number / Sort Code
              </label>
              <div className="mt-1" data-id="element-8274">
                <input type="text" name="routingNumber" id="routingNumber" value={formData.routingNumber} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8275" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-8276">
              <label htmlFor="swiftCode" className="block text-sm font-medium text-gray-700" data-id="element-8277">
                SWIFT / BIC Code
              </label>
              <div className="mt-1" data-id="element-8278">
                <input type="text" name="swiftCode" id="swiftCode" value={formData.swiftCode} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8279" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-8280">
              <label htmlFor="iban" className="block text-sm font-medium text-gray-700" data-id="element-8281">
                IBAN
              </label>
              <div className="mt-1" data-id="element-8282">
                <input type="text" name="iban" id="iban" value={formData.iban} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8283" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-8284">
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-8285">
                Currency
              </label>
              <div className="mt-1" data-id="element-8286">
                <select id="currency" name="currency" value={formData.currency} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8287">
                  {currencies.map(currency => <option key={currency} value={currency} data-id="element-8288">
                      {currency}
                    </option>)}
                </select>
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-8289">
              <label htmlFor="branchName" className="block text-sm font-medium text-gray-700" data-id="element-8290">
                Branch Name
              </label>
              <div className="mt-1" data-id="element-8291">
                <input type="text" name="branchName" id="branchName" value={formData.branchName} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8292" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-8293">
              <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-8294">
                Bank Country
              </label>
              <div className="mt-1" data-id="element-8295">
                <CountrySelector value={formData.country} onChange={handleCountryChange} locale={locale} data-id="element-8296" />
              </div>
            </div>

            <div className="sm:col-span-6" data-id="element-8297">
              <div className="flex items-center h-full" data-id="element-8298">
                <input id="isPrimary" name="isPrimary" type="checkbox" checked={formData.isPrimary} onChange={handleChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-8299" />
                <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-700" data-id="element-8300">
                  Is Primary Account
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3" data-id="element-8301">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8302">
              Cancel
            </button>
            <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8303">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-8304" />
              Save Bank Account
            </button>
          </div>
        </form>
      </div>
    </div>;
}